import { useState } from 'react';
import {
  Battery,
  BatteryCharging,
  BatteryWarning,
  Zap,
  Sun,
  DollarSign,
  ArrowDownToLine,
  ArrowUpFromLine,
  Rocket,
  Scissors,
  Loader2,
  Settings,
} from 'lucide-react';
import type { SiteLiveData, BatteryMode } from '../../types';
import { setBatteryMode, resendSellWithExportLimit } from '../../api/client';

// ESY inverter mode options for admin manual control
const INVERTER_MODES = [
  { code: 1, label: 'Regular Mode', description: 'Normal self-consumption' },
  { code: 3, label: 'Discharge Mode', description: 'Discharge to grid' },
  { code: 4, label: 'Charge Mode', description: 'Charge from grid' },
  { code: 5, label: 'Battery Energy Management', description: 'Scheduled optimization' },
];

interface BatteryStatusCardProps {
  data: SiteLiveData;
  siteId?: string;
  /** Shows the admin-only "Resend Sell + export limit" action. */
  isAdmin?: boolean;
}

export function BatteryStatusCard({ data, siteId, isAdmin = false }: BatteryStatusCardProps) {
  const { battery, solar, grid, optimizer } = data;
  const [modeChanging, setModeChanging] = useState(false);
  const [modeError, setModeError] = useState<string | null>(null);
  const [modeSuccess, setModeSuccess] = useState<string | null>(null);

  const handleModeChange = async (modeCode: number) => {
    if (!siteId || modeChanging) return;
    setModeChanging(true);
    setModeError(null);
    setModeSuccess(null);

    try {
      const result = await setBatteryMode(siteId, modeCode);
      if (result.status === 'error') {
        setModeError(result.message);
      } else {
        const modeName = INVERTER_MODES.find(m => m.code === modeCode)?.label || `Mode ${modeCode}`;
        setModeSuccess(`Switched to ${modeName}`);
        setTimeout(() => setModeSuccess(null), 5000);
      }
    } catch (e: unknown) {
      const errMsg = e instanceof Error ? e.message : 'Failed to change mode';
      setModeError(errMsg);
    } finally {
      setModeChanging(false);
    }
  };

  // Admin only: Sell mode + the current export limit in one command, the
  // same pair the controller sends on a plan event. Fixes units whose
  // export cap reverted on pattern entry / reconnect (Sell mode, no export).
  const handleResendSell = async () => {
    if (!siteId || modeChanging) return;
    setModeChanging(true);
    setModeError(null);
    setModeSuccess(null);

    try {
      const result = await resendSellWithExportLimit(siteId);
      if (result.status === 'error') {
        setModeError(result.message);
      } else {
        const pct = result.export_limit_percent;
        const limit = pct != null ? ` + export limit ${Math.round(pct)}%` : '';
        const applied =
          result.export_limit_applied === false ? ' (limit not applied)' : '';
        setModeSuccess(`Re-sent Discharge Mode${limit}${applied}`);
        setTimeout(() => setModeSuccess(null), 8000);
      }
    } catch (e: unknown) {
      const errMsg = e instanceof Error ? e.message : 'Failed to re-send sell command';
      setModeError(errMsg);
    } finally {
      setModeChanging(false);
    }
  };

  return (
    <div className="card p-4 space-y-4 h-full">
      {/* Header */}
      <div className="text-center border-b border-gray-200 dark:border-gray-600 pb-3">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
          Battery Optimizer
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">{optimizer.description}</p>
      </div>

      {/* Current Mode */}
      <ModeCard mode={optimizer.mode} exportLimitW={battery.export_limit_w ?? null} />

      {/* Manual Mode Control — available to the site owner (and admins) */}
      {siteId && (
        <ModeControl
          onModeChange={handleModeChange}
          onResendSell={isAdmin ? handleResendSell : undefined}
          changing={modeChanging}
          error={modeError}
          success={modeSuccess}
        />
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        {/* Battery SOC */}
        <StatusItem
          icon={getBatteryIcon(battery.soc_percent)}
          iconColor={getBatterySocColor(battery.soc_percent)}
          primary="Battery"
          secondary={`${battery.soc_percent.toFixed(0)}% State of Charge`}
        />

        {/* Solar */}
        <StatusItem
          icon={<Sun className="w-5 h-5" />}
          iconColor={getSolarColor(solar.power_w)}
          primary="Solar Production"
          secondary={`${solar.power_w.toFixed(0)}W`}
        />

        {/* Battery Signal */}
        <StatusItem
          icon={getBatterySignalIcon(optimizer.battery_signal_w)}
          iconColor={getBatterySignalColor(optimizer.battery_signal_w)}
          primary="Battery Signal"
          secondary={formatBatterySignal(optimizer.battery_signal_w)}
        />

        {/* Grid Signal */}
        <StatusItem
          icon={getGridSignalIcon(optimizer.grid_signal_w)}
          iconColor={getGridSignalColor(optimizer.grid_signal_w)}
          primary="Grid Signal"
          secondary={formatGridSignal(optimizer.grid_signal_w)}
        />
      </div>

      {/* Energy Prices */}
      <div className="bg-gray-50 dark:bg-brand-navy-light/50 rounded-lg p-3">
        <div className="flex items-center gap-2 mb-2">
          <DollarSign className={`w-5 h-5 ${getPriceColor(grid.import_price, grid.export_price)}`} />
          <span className="font-medium text-gray-900 dark:text-white">Energy Prices</span>
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-gray-500 dark:text-gray-400">Import: </span>
            <span className="font-semibold text-gray-900 dark:text-white">
              ${grid.import_price.toFixed(3)}/kWh
            </span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Export: </span>
            <span className="font-semibold text-gray-900 dark:text-white">
              ${grid.export_price.toFixed(3)}/kWh
            </span>
          </div>
        </div>
      </div>

    </div>
  );
}

function ModeCard({ mode, exportLimitW }: { mode: BatteryMode; exportLimitW: number | null }) {
  const { icon, color, label } = getModeConfig(mode);
  // "Discharging (500W)": append the active export clamp when the battery is
  // sending power out and SolanIQ has restricted feed-in.
  const showLimit =
    exportLimitW != null && (mode === 'discharging' || mode === 'maximum_sell');
  const text = showLimit ? `${label} (${Math.round(exportLimitW)}W)` : label;

  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg ${color}`}>
      {icon}
      <div>
        <p className="text-xs text-gray-500 dark:text-gray-400">Current Mode</p>
        <p className="font-semibold text-gray-900 dark:text-white">{text}</p>
      </div>
    </div>
  );
}

interface StatusItemProps {
  icon: React.ReactNode;
  iconColor: string;
  primary: string;
  secondary: string;
}

function StatusItem({ icon, iconColor, primary, secondary }: StatusItemProps) {
  return (
    <div className="flex items-center gap-3 p-2 rounded-lg bg-gray-50 dark:bg-brand-navy-light/50">
      <div className={iconColor}>{icon}</div>
      <div>
        <p className="text-xs text-gray-500 dark:text-gray-400">{primary}</p>
        <p className="font-medium text-gray-900 dark:text-white text-sm">{secondary}</p>
      </div>
    </div>
  );
}

interface ModeControlProps {
  onModeChange: (modeCode: number) => void;
  /** Admin only: present when the viewer may re-send Sell + export limit. */
  onResendSell?: () => void;
  changing: boolean;
  error: string | null;
  success: string | null;
}

function ModeControl({ onModeChange, onResendSell, changing, error, success }: ModeControlProps) {
  const [selectedMode, setSelectedMode] = useState<number>(1);

  return (
    <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/50 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-2">
        <Settings className="w-4 h-4 text-amber-600 dark:text-amber-400" />
        <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
          Manual Mode Control
        </span>
      </div>

      <p className="mb-2 text-xs text-amber-600 dark:text-amber-500">
        Overrides optimisation until changed. The inverter applies the new mode
        after a short delay.
      </p>

      <div className="flex gap-2">
        <select
          value={selectedMode}
          onChange={(e) => setSelectedMode(Number(e.target.value))}
          disabled={changing}
          className="flex-1 text-sm rounded-md border border-amber-300 dark:border-amber-700 bg-card dark:bg-brand-navy-light text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:ring-amber-500 focus:border-amber-500"
        >
          {INVERTER_MODES.map((m) => (
            <option key={m.code} value={m.code}>
              {m.label}
            </option>
          ))}
        </select>
        <button
          onClick={() => onModeChange(selectedMode)}
          disabled={changing}
          className="px-3 py-1.5 text-sm font-medium rounded-md bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
        >
          {changing ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            'Set'
          )}
        </button>
      </div>

      {/* Description */}
      <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-500">
        {INVERTER_MODES.find(m => m.code === selectedMode)?.description}
      </p>

      {/* Admin only: Sell + current export limit, as the controller sends it */}
      {onResendSell && (
        <div className="mt-2 pt-2 border-t border-amber-200 dark:border-amber-800/50">
          <button
            onClick={onResendSell}
            disabled={changing}
            data-testid="resend-sell-button"
            className="w-full px-3 py-1.5 text-sm font-medium rounded-md border border-amber-600 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
          >
            {changing ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              'Resend Sell + export limit'
            )}
          </button>
          <p className="mt-1 text-xs text-amber-600 dark:text-amber-500">
            Admin: re-sends Discharge Mode with the last export limit SolanIQ applied,
            for inverters that sit in Sell mode without exporting.
          </p>
        </div>
      )}

      {/* Error */}
      {error && (
        <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">{error}</p>
      )}

      {/* Success */}
      {success && (
        <p className="mt-1.5 text-xs text-green-600 dark:text-green-400">{success}</p>
      )}
    </div>
  );
}

// Helper functions
function getModeConfig(mode: BatteryMode) {
  const configs: Record<BatteryMode, { icon: React.ReactNode; color: string; label: string }> = {
    idle: {
      icon: <Battery className="w-6 h-6 text-gray-500" />,
      color: 'bg-gray-100 dark:bg-gray-700',
      label: 'Idle',
    },
    charging: {
      icon: <BatteryCharging className="w-6 h-6 text-blue-500" />,
      color: 'bg-blue-100 dark:bg-blue-900/30',
      label: 'Charging',
    },
    discharging: {
      icon: <Battery className="w-6 h-6 text-teal-500" />,
      color: 'bg-teal-100 dark:bg-teal-900/30',
      label: 'Discharging',
    },
    grid_charge: {
      icon: <BatteryCharging className="w-6 h-6 text-blue-500" />,
      color: 'bg-blue-100 dark:bg-blue-900/30',
      label: 'Grid Charge',
    },
    solar_charge: {
      icon: <Sun className="w-6 h-6 text-amber-500" />,
      color: 'bg-amber-100 dark:bg-amber-900/30',
      label: 'Solar Charge',
    },
    extreme_buy: {
      icon: <Zap className="w-6 h-6 text-yellow-500" />,
      color: 'bg-yellow-100 dark:bg-yellow-900/30',
      label: 'Extreme Buy',
    },
    maximum_sell: {
      icon: <DollarSign className="w-6 h-6 text-green-500" />,
      color: 'bg-green-100 dark:bg-green-900/30',
      label: 'Maximum Sell',
    },
    curtail: {
      icon: <Scissors className="w-6 h-6 text-orange-500" />,
      color: 'bg-orange-100 dark:bg-orange-900/30',
      label: 'Curtail',
    },
    curtail_boost: {
      icon: <Rocket className="w-6 h-6 text-purple-500" />,
      color: 'bg-purple-100 dark:bg-purple-900/30',
      label: 'Curtail Boost',
    },
  };

  return configs[mode] || configs.idle;
}

function getBatteryIcon(soc: number) {
  if (soc >= 90) return <Battery className="w-5 h-5" />;
  if (soc >= 50) return <Battery className="w-5 h-5" />;
  if (soc >= 20) return <Battery className="w-5 h-5" />;
  return <BatteryWarning className="w-5 h-5" />;
}

function getBatterySocColor(soc: number): string {
  if (soc >= 50) return 'text-green-500';
  if (soc >= 20) return 'text-orange-500';
  return 'text-red-500';
}

function getSolarColor(power: number): string {
  if (power > 3000) return 'text-yellow-500';
  if (power > 1000) return 'text-amber-500';
  if (power > 0) return 'text-orange-500';
  return 'text-gray-400';
}

function getBatterySignalIcon(power: number) {
  if (power > 100) return <ArrowUpFromLine className="w-5 h-5" />;
  if (power < -100) return <BatteryCharging className="w-5 h-5" />;
  return <Battery className="w-5 h-5" />;
}

function getBatterySignalColor(power: number): string {
  if (power > 100) return 'text-teal-500';
  if (power < -100) return 'text-blue-500';
  return 'text-gray-400';
}

function formatBatterySignal(power: number): string {
  if (power > 100) return `Discharge ${power.toFixed(0)}W`;
  if (power < -100) return `Charge ${Math.abs(power).toFixed(0)}W`;
  return `Hold (${power.toFixed(0)}W)`;
}

function getGridSignalIcon(power: number) {
  if (power > 0) return <ArrowDownToLine className="w-5 h-5" />;
  return <ArrowUpFromLine className="w-5 h-5" />;
}

function getGridSignalColor(power: number): string {
  if (power > 0) return 'text-red-500';
  return 'text-green-500';
}

function formatGridSignal(power: number): string {
  if (power > 0) return `Import ${power.toFixed(0)}W`;
  if (power < 0) return `Export ${Math.abs(power).toFixed(0)}W`;
  return 'Balanced';
}

function getPriceColor(importPrice: number, exportPrice: number): string {
  if (importPrice < 0) return 'text-yellow-500';
  if (exportPrice < 0) return 'text-orange-500';
  if (importPrice < 0.15) return 'text-green-500';
  if (importPrice > 0.50) return 'text-red-500';
  return 'text-blue-500';
}
